import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  OnDestroy,
  Output,
  inject,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { IncomingCallModalComponent } from '../incoming-call-modal/incoming-call-modal.component';
import {
  NgbModal,
  NgbModalOptions,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { CommonModule } from '@angular/common';
import { TwilioService } from '../../../patient-on-boarding-portal/service/twilio.service';
import { Call, Device } from '@twilio/voice-sdk';
import { Subscription } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { DashboardService } from '../../../patient-on-boarding-portal/service/dashboard.service';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { TextAreaComponent } from '../text-area/text-area.component';
import { CommunicationLogsService } from '../../../care-manager-portal/components/communication-logs/services/communication-logs.service';
import { ToasterMessageService } from '../../services/topbar-toaster/toaster-message.service';
import { GlobalService } from '../../services/common/global.service';
import { SelectComponent } from '../select/select.component';
import { CommonLookupService } from '../../services/lookup/common-lookup.service';
import { catchError, map, of, retry, timer } from 'rxjs';
import { IncomingCallRouterService } from '../../services/incoming-call-router.service';

@Component({
  selector: 'app-incoming-call-notification',
  imports: [IncomingCallModalComponent, CommonModule, ReactiveFormsModule, TextAreaComponent, SelectComponent],
  templateUrl: './incoming-call-notification.component.html',
  styleUrls: ['./incoming-call-notification.component.css'],
})
export class IncomingCallNotificationComponent implements OnInit, OnDestroy {
  /**
   * Holds the latest incoming call object to be passed to the modal for stacking/logging.
   */
  latestIncomingCall: any = null;
  @Input() data: {
    name?: string;
    number?: string;
    clinic?: string;
    avatar?: string;
    patientId?: string;
    callSid?: string;
    twilioNumber?: string;
  } = {
    name: 'Sarah Johnson',
    number: '+1 (555) 123-4567',
    clinic: 'Clinic Name here',
    avatar: '',
    patientId: '',
    callSid: '',
  };

  @Output() queueListUpdated = new EventEmitter<any[]>();

  constructor(
    private dashboardService: DashboardService,
    private twilio: TwilioService,
    private fb: FormBuilder,
  ) {
    this.generatedSummaryForm = this.fb.group({
      generatedSummary: ['', [Validators.required]],
      programId: ['', [Validators.required]],
      interactionTypeId: ['', [Validators.required]],
    });
  }

  private modalRef: NgbModalRef | null = null;
  private generateSummaryModalRef: NgbModalRef | null = null;
  private modalService = inject(NgbModal);
  private twilioService = inject(TwilioService);
  private communicationService = inject(CommunicationLogsService);
  private toastService = inject(ToasterMessageService);
  private globalService = inject(GlobalService);
  private commonLookupService = inject(CommonLookupService);
  private callRouter = inject(IncomingCallRouterService);
  private deviceSub: Subscription | null = null;
  private activeCallSub: Subscription | null = null;

  @ViewChild('generatedSummaryModal') generatedSummaryModal!: TemplateRef<any>;
  generatedSummaryForm: FormGroup;
  summary: string = '';
  programOptions: { id: any; value: string }[] = [];
  interactionTypeOptions: { id: any; value: string }[] = [];
  private ivrSelection: string | number = '';
  private responseTime: string | number = '';
  private username: string = '';

  dismissIncomingCall: boolean = false;
  private incomingCall: Call | null = null;
  // Reference to the call this component last marked as accepted/active,
  // captured independently of TwilioService's mutable activeCall state
  // (which onEndOngoingCall clears synchronously, before the async
  // 'disconnect' event fires — comparing against that live state would
  // make a genuine own-call disconnect look "stale").
  private trackedActiveCall?: Call;
  showNotification: boolean = false;
  public isCallAccepted: boolean = false;
  // Gates the Notes/Approve section of the incoming-call modal — only true
  // once the after-call summary for the current call has been generated
  // and saved, so an agent can't approve/resolve the patient before that.
  public summarySaved: boolean = false;
  runningCallTimer: string = '00:00:00';
  showOngoingCallCard: boolean = false;
  ongoingCallData: {
    name?: string;
    number?: string;
    clinic?: string;
  } | null = null;

  callDuration: number = 0;
  private callTimerInterval: any = null;

  @ViewChild('callModal', { static: true }) callModal!: TemplateRef<any>;

  get initials(): string {
    const name = this.data?.name || '';
    const parts = name.trim().split(/\s+/);
    const first = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
    const last =
      parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
    return (first + last).slice(0, 2);
  }

  getInitials(name: string): string {
    const parts = (name || '').trim().split(/\s+/);
    const first = parts[0] ? parts[0].charAt(0).toUpperCase() : '';
    const last =
      parts.length > 1 ? parts[parts.length - 1].charAt(0).toUpperCase() : '';
    return (first + last).slice(0, 2);
  }

  // controls whether name and number are swapped
  isSwapped = false;

  /** If true, will automatically toggle the swap state. */
  @Input() autoSwap = true;

  /** Interval in milliseconds between swaps when `autoSwap` is enabled. */
  @Input() autoSwapInterval = 3000;

  private _autoSwapIntervalId: any = null;
  private _autoSwapTimer: any = null;
  conferenceId: string = '';
  callSid: string = '';
  fromNumber: string = '';

  toggleSwap(): void {
    this.isSwapped = !this.isSwapped;
  }

  ngOnInit(): void {
    // Set up incoming call listener from Twilio Device
    this.setupIncomingCallListener();
    this.setupActiveCallSync();
    this.loadInteractionTypes();

    if (!this.autoSwap) {
      return;
    }

    // start with a short delayed first swap for a pleasant effect,
    // then continue toggling on the configured interval
    this._autoSwapTimer = setTimeout(() => {
      this.isSwapped = true;
      this._autoSwapIntervalId = setInterval(() => {
        this.isSwapped = !this.isSwapped;
      }, this.autoSwapInterval);
    }, 800);
  }

  private setupActiveCallSync(): void {
    try {
      this.activeCallSub = (this.twilioService as any).activeCall$?.subscribe(
        (activeCall: Call | undefined) => {
          if (activeCall) {
            this.isCallAccepted = true;
            if (!this.callTimerInterval) {
              this.startCallTimer();
            }
          } else {
            if (this.callTimerInterval) {
              this.resetCallTimer();
            }
            if (!this.showNotification) {
              this.isCallAccepted = false;
              this.showOngoingCallCard = false;
            }
          }
        },
      );
    } catch (error) {
      console.warn('Could not set up active call sync listener:', error);
    }
  }

  private setupIncomingCallListener(): void {
    console.log('calling');
    // Note: This assumes Twilio Device is initialized elsewhere
    // and exposes a way to listen to incoming calls
    // The TwilioService.initialize() method should set up the device
    // and you may need to create a method in TwilioService to handle incoming calls

    // For now, we'll listen via a potential event emitter or callback
    // This will be triggered when an incoming call arrives
    // Subscribe to device changes so we always attach the incoming handler
    try {
      this.deviceSub = (this.twilioService as any).device$
        .pipe(distinctUntilChanged())
        .subscribe((device: Device | undefined) => {
          console.log(
            'Twilio Device change detected for incoming listener:',
            device,
          );
          if (!device) {
            return;
          }

          // Remove any existing incoming handler to avoid duplicates
          try {
            if ((device as any).off) {
              (device as any).off('incoming');
            }
          } catch (err) {
            // if off is not available, ignore
          }

          device.on('incoming', (call: Call) => {
            console.log('Twilio Device incoming call event:', call);
            this.handleIncomingCall(call);
          });
        });
    } catch (error) {
      console.warn(
        'Could not set up Twilio incoming call listener subscription:',
        error,
      );
    }
  }

  private handleIncomingCall(call: Call): void {
    // A screen can take calls of its referralType (e.g. NP batch calling) instead of this modal.
    if (this.callRouter.tryRoute(call, this.routingParams(call))) {
      return;
    }

    const activeCall = (this.twilioService as any).getActiveCall?.();
    const hasOngoingActiveCall = !!activeCall || this.isCallAccepted;

    // If a call is already active, set latestIncomingCall for the modal
    if (hasOngoingActiveCall) {
      // Prepare a call object for the modal (mimic the data structure)
      const raw = (call as any).customParameters;
      const params = (call as any).parameters || {};
      let customParams: any = {};
      if (Array.isArray(raw)) {
        raw.forEach((item: any) => {
          if (item && typeof item === 'object') {
            const key = Object.keys(item)[0];
            customParams[key] = item[key];
          }
        });
      } else if (raw instanceof Map) {
        raw.forEach((value: any, key: any) => {
          customParams[key] = value;
        });
      } else if (raw && typeof raw === 'object') {
        Object.values(raw).forEach((item: any) => {
          if (item && typeof item === 'object') {
            const key = Object.keys(item)[0];
            customParams[key] = item[key];
          }
        });
      }
      const callObj = {
        name: customParams.patientName,
        number: customParams.patientNumber,
        clinic: customParams.clinic || customParams.ClinicName,
        avatar: '',
        patientId: customParams.patientId || '',
        sid: customParams.CallSid || '',
        twilioNumber: params.From || params.from,
      };

      this.latestIncomingCall = callObj;
      // Optionally log here as well
      console.log('Passing new incoming call to modal:', callObj);
    }
    this.incomingCall = call;
    this.dismissIncomingCall = false;
    this.showNotification = true; // Show notification when call arrives
    this.isCallAccepted = hasOngoingActiveCall;

    // Extract call parameters
    const raw = (call as any).customParameters;
    const params = (call as any).parameters || {};

    let customParams: any = {};

    // Case 1: Real Array
    if (Array.isArray(raw)) {
      raw.forEach((item: any) => {
        if (item && typeof item === 'object') {
          const key = Object.keys(item)[0];
          customParams[key] = item[key];
        }
      });
    }
    // Case 2: Map
    else if (raw instanceof Map) {
      raw.forEach((value: any, key: any) => {
        customParams[key] = value;
      });
    }
    // Case 3: Object with numeric keys (your error case)
    else if (raw && typeof raw === 'object') {
      Object.values(raw).forEach((item: any) => {
        if (item && typeof item === 'object') {
          const key = Object.keys(item)[0];
          customParams[key] = item[key];
        }
      });
    }

    // Extract CallSid and From from parameters
    this.callSid = params.CallSid || '';
    const fromNumber = params.From || params.from || '';
    this.fromNumber = fromNumber;

    // Populate component data
    this.data = {
      name: customParams.patientName || customParams.ComingCall,
      number: customParams.patientNumber,
      clinic: customParams.clinic || customParams.ClinicName,
      avatar: '',
      patientId: customParams.patientId || '',
      callSid: this.callSid,
      twilioNumber: fromNumber,
    };

    this.ivrSelection = customParams.ivrSelection ?? '';
    this.responseTime = customParams.responseTime ?? '';
    this.username = customParams.username ?? '';

    console.log('CallSid:', this.callSid);
    console.log('From:', fromNumber);
    console.log(customParams);
    console.log(this.data);
    console.log('📞 Incoming call received:', this.data);

    // Set up call event handlers
    call.on('accept', () => {
      console.log('✅ Call accepted:', this.data.name);
      localStorage.setItem('callActive', 'true');
      this.twilioService.setActiveCall(call);
      this.trackedActiveCall = call;
      this.isCallAccepted = true;
      this.summarySaved = false;
      this.callDuration = 0;
      this.runningCallTimer = this.formatCallDuration(this.callDuration);
      this.startCallTimer();
      this.ongoingCallData = {
        name: this.data?.name || '',
        number: this.data?.number || '',
        clinic: this.data?.clinic || '',
      };
    });

    call.on('disconnect', (params) => {
      console.log('📴 Call disconnected', params);

      const isDisconnectedActiveCall = this.trackedActiveCall === call;

      if (!isDisconnectedActiveCall && this.isCallAccepted) {
        this.showNotification = false;
        this.dismissIncomingCall = false;
        this.incomingCall = null;
        this.latestIncomingCall = null;
        return;
      }

      const wasAccepted = this.isCallAccepted;
      this.trackedActiveCall = undefined;
      const endedPatientId = this.data?.patientId;
      const endedCallSid = this.callSid;
      const endedCallDuration = this.callDuration;
      const endedIvrSelection = this.ivrSelection;
      const endedResponseTime = this.responseTime;
      const endedUsername = this.username;
      const endedCallerName = this.data?.name || '';
      const endedConfSid = this.conferenceId || '';
      const endedCallDateAndTime = new Date().toISOString();

      localStorage.setItem('callActive', 'false');
      this.twilioService.setActiveCall(undefined);
      console.log(this.incomingCall);
      if (this.incomingCall) {
        this.incomingCall.disconnect?.();
      }
      this.dismissIncomingCall = true;
      this.showNotification = false; // Hide notification on disconnect
      this.showOngoingCallCard = false;
      this.incomingCall = null;
      this.isCallAccepted = false;
      this.ongoingCallData = null;
      this.resetCallTimer();

      // After-call summary generation is temporarily disabled.
      // Only generate a summary for calls that were actually answered and tied to a patient
      if (wasAccepted && endedPatientId) {
        this.generateAfterCallSummary(
          endedPatientId,
          endedCallSid,
          endedCallDuration,
          endedIvrSelection,
          endedResponseTime,
          endedUsername,
          endedCallerName,
          endedConfSid,
          endedCallDateAndTime,
        );
      }

      // Close the modal when the call ends
      this.handleModalDismiss();
    });

    call.on('cancel', () => {
      console.log('❌ Incoming call cancelled');
      if (hasOngoingActiveCall) {
        this.dismissIncomingCall = false;
        this.showNotification = false;
        this.incomingCall = null;
        this.latestIncomingCall = null;
        return;
      }

      // Only close modal if this was the active call, not just an incoming call while already on a call
      if (!this.isCallAccepted) {
        this.dismissIncomingCall = true;
        this.showNotification = false; // Hide notification on cancel
        this.showOngoingCallCard = false;
        this.incomingCall = null;
        this.latestIncomingCall = null; // Clear the latest incoming call data
        this.ongoingCallData = null;
        this.resetCallTimer();
        // this.handleModalDismiss();
      } else {
        // If already on a call, do not close the modal
        // Just ignore the cancelled incoming call
        this.dismissIncomingCall = false;
      }
    });

    call.on('error', (error: any) => {
      console.error('❗ Call error:', error);
      if (hasOngoingActiveCall) {
        this.dismissIncomingCall = false;
        this.showNotification = false;
        this.incomingCall = null;
        this.latestIncomingCall = null;
        return;
      }

      this.dismissIncomingCall = true;
      this.showNotification = false; // Hide notification on error
      this.showOngoingCallCard = false;
      this.incomingCall = null;
      this.isCallAccepted = false;
      this.ongoingCallData = null;
      this.resetCallTimer();
      // Close modal if open when there's an error
      this.handleModalDismiss();
    });
  }

  /** Custom parameters (any of Twilio's shapes) merged over the call parameters. */
  private routingParams(call: Call): Record<string, string> {
    const out: Record<string, string> = { ...((call as any).parameters || {}) };
    const raw = (call as any).customParameters;
    if (raw instanceof Map) {
      raw.forEach((value: any, key: any) => (out[key] = value));
    } else if (raw && typeof raw === 'object') {
      Object.values(raw).forEach((item: any) => {
        if (item && typeof item === 'object') {
          const key = Object.keys(item)[0];
          out[key] = item[key];
        }
      });
    }
    return out;
  }

  ngOnDestroy(): void {
    if (this._autoSwapTimer) {
      clearTimeout(this._autoSwapTimer);
      this._autoSwapTimer = null;
    }
    if (this._autoSwapIntervalId) {
      clearInterval(this._autoSwapIntervalId);
      this._autoSwapIntervalId = null;
    }
    // unsubscribe from device observable and remove incoming listener
    try {
      if (this.deviceSub) {
        this.deviceSub.unsubscribe();
        this.deviceSub = null;
      }
      if (this.activeCallSub) {
        this.activeCallSub.unsubscribe();
        this.activeCallSub = null;
      }
      const device = (this.twilioService as any).getDevice?.();
      if (device && (device as any).off) {
        (device as any).off('incoming');
      }
    } catch (err) {
      // ignore cleanup errors
    }
    this.stopCallTimer();
  }

  openModal(content: TemplateRef<HTMLElement>, options: NgbModalOptions) {
    if (this.modalRef) {
      return;
    }

    const modalOptions: NgbModalOptions = {
      ...options,
      backdrop: 'static',
      keyboard: false,
      size: 'lg',
      modalDialogClass: 'custom-modal',
      centered: true,
    };

    // this.modalService.open(content, modalOptions);
    this.modalRef = this.modalService.open(content, modalOptions); // Store the modal reference
  }

  handleModalDismiss() {
    if (this.modalRef) {
      this.modalRef.close();
      this.modalRef = null; // Ensure modalRef is reset
    }

    // After modal is dismissed, restore notification visibility
    // only if the call hasn't been dismissed/cancelled.
    this.showNotification = !this.dismissIncomingCall && !this.isCallAccepted;
    this.showOngoingCallCard = this.isCallAccepted && !this.dismissIncomingCall;
    this.latestIncomingCall = null; // Clear the latest incoming call data
    if (!this.showOngoingCallCard) {
      this.resetCallTimer();
      this.ongoingCallData = null;
    }
  }

  onClick(event: Event) {
    if (event) {
      event.preventDefault();
    }

    // Accept the incoming call (if present)
    if (this.incomingCall) {
      this.incomingCall.accept();

      this.isCallAccepted = true;

      // Fetch conference ID using patientId if available
      const patientId = this.data?.patientId;
      if (patientId) {
        this.dashboardService
          .GetConferenceId(patientId)
          .subscribe((data: any) => {
            // You can handle the conferenceId here, e.g., store or emit it
            this.conferenceId = data.conferenceId;
            // Optionally, call a handler if needed
            if (typeof (this as any).onConferenceIdFetched === 'function') {
              (this as any).onConferenceIdFetched(data.conferenceId);
            }
          });
      }
    }

    // Hide the inline notification so only the modal is visible
    this.showNotification = false;
    this.showOngoingCallCard = false;

    this.openModal(this.callModal, {});
  }

  activeConnection: any = null;

  onEndOngoingCall(event: Event) {
    if (event) {
      event.preventDefault();
    }

    try {
      // Prefer the TwilioService-level disconnect which clears service state
      const twilioServiceDisconnect = (this.twilioService as any).disconnectCall?.bind(
        this.twilioService,
      );
      const sharedCall = (this.twilioService as any).getActiveCall?.();

      if (twilioServiceDisconnect) {
        // This will call device.disconnectAll(), clear local state, and update observables
        twilioServiceDisconnect();
      } else if (sharedCall?.disconnect) {
        // Fallback: disconnect the active Call instance
        sharedCall.disconnect();
      } else if (this.activeConnection && this.activeConnection.disconnect) {
        this.activeConnection.disconnect();
      } else if (this.incomingCall && this.incomingCall.disconnect) {
        this.incomingCall.disconnect();
      }
      console.log('Ended call', sharedCall || this.activeConnection);
      // Ensure internal flags are reset
      try {
        (this.twilioService as any).setActiveCall?.(undefined);
      } catch (e) {
        // ignore if not available
      }
    } catch (err) {
      console.warn('Failed to properly disconnect call', err);
      try {
        this.twilioService.disconnectCall();
      } catch (e) {}
    }

    this.dismissIncomingCall = true;
    this.showNotification = false;
    // Note: isCallAccepted is intentionally left untouched here. The Twilio
    // 'disconnect' event fires asynchronously after disconnect() above and
    // is what actually resets isCallAccepted and triggers
    // generateAfterCallSummary via its `wasAccepted` check. Clearing it here
    // would race ahead of that event and cause the after-call summary to be
    // silently skipped for locally-initiated hangups.
    this.showOngoingCallCard = false;
    this.ongoingCallData = null;
    this.incomingCall = null;
    this.latestIncomingCall = null;
    this.activeConnection = null;
    this.resetCallTimer();
    this.handleModalDismiss();
  }

  onEndCall(event: Event) {
    console.log('here', this.incomingCall);
    // Ignore/reject the incoming call
    if (event) {
      event.preventDefault();
    }

    if (this.incomingCall) {
      try {
        // Prefer rejecting an incoming ringing call if supported
        if (typeof (this.incomingCall as any).reject === 'function') {
          (this.incomingCall as any).reject();
        } else if (
          typeof (this.incomingCall as any).disconnect === 'function'
        ) {
          (this.incomingCall as any).disconnect();
        } else {
          // fallback: disconnect all via the service
          this.twilioService.disconnectCall();
        }
      } catch (err) {
        console.warn('Error ending incoming call:', err);
        try {
          this.twilioService.disconnectCall();
        } catch (e) {}
      }
    } else {
      try {
        this.twilioService.disconnectCall();
      } catch (e) {}
    }

    this.twilioService.setActiveCall(undefined);
    this.dismissIncomingCall = true;
    this.isCallAccepted = false;
    this.showOngoingCallCard = false;
    this.ongoingCallData = null;
    this.resetCallTimer();
    // Ensure modal is closed when user ends the call from UI
    this.handleModalDismiss();
  }

  onQueueListUpdated(queueList: any[]): void {
    this.queueListUpdated.emit(queueList);
  }

  onStartNextCall(data: any): void {
    this.isCallAccepted = true;
    this.showNotification = false;
    this.dismissIncomingCall = false;
    this.ongoingCallData = {
      name: data?.name || '',
      number: data?.number || '',
      clinic: data?.clinic || '',
    };
    this.data = {
      ...this.data,
      name: data?.name || this.data?.name || '',
      number: data?.number || this.data?.number || '',
      clinic: data?.clinic || this.data?.clinic || '',
      patientId: data?.patientId || this.data?.patientId || '',
    };
  }

    openCallDetail(event?: Event): void {
    if (event) {
      event.preventDefault();
    }
    this.showOngoingCallCard = false;
    this.openModal(this.callModal, {});
  }

  openCallModalFromTopbar(): void {
    this.openCallDetail();
  }

  private startCallTimer(): void {
    this.stopCallTimer();
    this.callTimerInterval = setInterval(() => {
      this.callDuration++;
      this.runningCallTimer = this.formatCallDuration(this.callDuration);
    }, 1000);
  }

  private stopCallTimer(): void {
    if (this.callTimerInterval) {
      clearInterval(this.callTimerInterval);
      this.callTimerInterval = null;
    }
  }

  private resetCallTimer(): void {
    this.stopCallTimer();
    this.callDuration = 0;
    this.runningCallTimer = this.formatCallDuration(this.callDuration);
  }

  formatDisplayNumber(number?: string): string {
    if (!number) return '';
    const digits = number.replace(/\D/g, '');
    if (digits.length === 11) {
      return '+' + digits;
    }
    return digits || number;
  }

  formatCallDuration(seconds: number): string {
    const h = Math.floor(seconds / 3600)
      .toString()
      .padStart(2, '0');
    const m = Math.floor((seconds % 3600) / 60)
      .toString()
      .padStart(2, '0');
    const s = Math.floor(seconds % 60)
      .toString()
      .padStart(2, '0');
    return `${h}:${m}:${s}`;
  }

  private loadInteractionTypes(): void {
    this.commonLookupService
      .getPatientInteraction()
      .pipe(
        retry(2),
        catchError(() => of([])),
      )
      .subscribe((interactionTypes: any) => {
        const callTypes = (Array.isArray(interactionTypes) ? interactionTypes : []).filter(
          (ct: any) => ct.interaction_type === 'Call_Type',
        );
        this.interactionTypeOptions = callTypes.map((ct: any) => ({
          id: ct.interaction_type_id,
          value: ct.interaction_name,
        }));
      });
  }

  generateAfterCallSummary(
    patientId: string,
    callSid: string,
    callDuration: number,
    ivrSelection: string | number,
    responseTime: string | number,
    callerUsername: string,
    callerName: string,
    confSid: string,
    callDateAndTime: string,
  ): void {
    if (!patientId) {
      return;
    }

    let username = callerUsername;
    if (!username) {
      const userSettings = JSON.parse(this.globalService.getUserSettings() || '{}');
      const userInfo = userSettings?.userInfo || {};
      username = [userInfo.first_name, userInfo.last_name].filter(Boolean).join(' ');
    }

    const summary$ = (
      this.USE_MOCK_AFTER_CALL_SUMMARY
        ? of(this.MOCK_AFTER_CALL_SUMMARY)
        : this.communicationService.getLiveCallSummary(
            Number(patientId),
            ivrSelection,
            responseTime,
            username,
            callerName,
            confSid,
            callDateAndTime,
          )
    ).pipe(
      map((data: any) => {
        let parsed: any = data;

        if (typeof parsed === 'string') {
          try {
            parsed = JSON.parse(parsed);
          } catch {
            parsed = null;
          }
        }

        const successOk = parsed?.success === undefined || parsed?.success === true;
        const hasSummary = parsed && typeof parsed === 'object' && parsed.summary;

        if (!successOk || !hasSummary) {
          throw new Error('Failed to generate after-call summary.');
        }

        return parsed;
      }),
      // Every time the after-call summary fetch fails (network error or an
      // unsuccessful/empty response), retry with backing-off delays instead
      // of giving up immediately — live-call summarization can lag briefly
      // behind the call actually ending.
      retry({
        count: this.AFTER_CALL_SUMMARY_RETRY_DELAYS_MS.length,
        delay: (error, retryCount) => {
          const delayMs = this.AFTER_CALL_SUMMARY_RETRY_DELAYS_MS[retryCount - 1];
          console.warn(
            `After-call summary fetch failed, retrying in ${delayMs / 1000}s (attempt ${retryCount}/${this.AFTER_CALL_SUMMARY_RETRY_DELAYS_MS.length})`,
            error,
          );
          return timer(delayMs);
        },
      }),
    );

    summary$.subscribe({
        next: (parsed: any) => {
          this.summary = Object.entries(parsed.summary)
            .map(([key, value]) => {
              const header = key.toUpperCase();
              let text: string;
              if (Array.isArray(value)) {
                text = value
                  .map((item: any) =>
                    item && typeof item === 'object'
                      ? `${item.name}${item.priority ? ` (${item.priority})` : ''}`
                      : String(item),
                  )
                  .join(', ');
              } else {
                text = String(value ?? '');
              }
              return `${header}:\n${text}`;
            })
            .join('\n\n');

          const programName = parsed?.summary?.['Program Name'];
          this.programOptions = Array.isArray(parsed?.activePrograms)
            ? parsed.activePrograms.map((p: any) => ({
                id: p.id,
                value: p.programname,
              }))
            : [];
          const matchedProgram = Array.isArray(parsed?.activePrograms)
            ? parsed.activePrograms.find(
                (p: any) => p?.programname === programName,
              )
            : null;

          this.generatedSummaryForm.patchValue({
            generatedSummary: this.summary,
            programId: matchedProgram?.id ?? '',
          });

          this.summaryPatientId = Number(patientId);
          this.summaryCallSid = callSid;
          this.summaryCallDuration = callDuration;
          this.summaryCallDateAndTime = this.formatCallDateAndTime(new Date());
          this.summaryConfSid = confSid;

          this.openGeneratedSummaryModal();
        },
        error: () => {
          this.toastService.errroMessage('Failed to generate after-call summary.', true);
        },
      });
  }

  summaryPatientId: number = 0;
  summaryCallSid: string = '';
  summaryCallDuration: number = 0;
  summaryCallDateAndTime: string = '';
  summaryProgramId: number | null = null;
  summaryInteractionTypeId: number | null = null;
  summaryConfSid: string = '';

  /** Formats a Date as MM/DD/YYYY hh:mm AM/PM, e.g. "07/06/2026 09:03 AM". */
  private formatCallDateAndTime(date: Date): string {
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const year = date.getFullYear();
    let hours = date.getHours();
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const hoursStr = String(hours).padStart(2, '0');
    return `${month}/${day}/${year} ${hoursStr}:${minutes} ${ampm}`;
  }

  /** Backoff delays applied between retries when the after-call summary fetch fails. */
  private readonly AFTER_CALL_SUMMARY_RETRY_DELAYS_MS = [5000, 5000, 5000];

  /** TEST-ONLY: when true, skips the getLiveCallSummary API call and uses MOCK_AFTER_CALL_SUMMARY instead. Must be false before merging. */
  private readonly USE_MOCK_AFTER_CALL_SUMMARY = false;
  private readonly MOCK_AFTER_CALL_SUMMARY = {
    success: true,
    message: 'IVR live call summary generated successfully.',
    patientId: 61713,
    summary: {
      'Caller Name': 'Hassan Murtaza',
      'IVR Selection': '1 - Patient',
      Routing: 'Live answer attempt Sandrine answered at 12:30 PM',
      'Response Time': '50 sec',
      'Call Date & Time': '2026-07-11T17:30:00+00:00',
      Transcript:
        "Agent [04:970] : Can you briefly tell me the reason for your call?\nPatient [04:790] : I'm having a chest pain.\nAgent [115:610] : Okay. Let me connect you with someone in care team. Please wait. We weren't able to connect you with a team member right now. But someone will call you back immediately. Please stay available at this number. If this is a medical emergency, please hang up up and dial 911. Thank you for calling CareGenics.",
      Summary:
        'The patient reported having chest pain during the call. The agent attempted to connect the patient with a care team member. The call ended with the agent informing the patient that someone would call back immediately.',
      Outcome: 'Callback requested',
      Disposition: 'Awaiting callback',
      'Program Name': 'RPM',
      Priority: 'P1',
      Tags: [
        {
          name: 'Urgent / Symptom',
          priority: 'P1',
        },
      ],
    },
    activePrograms: [
      { id: 1, programname: 'RPM' },
      { id: 7, programname: 'CCM' },
    ],
  };

  openGeneratedSummaryModal(): void {
    this.generateSummaryModalRef = this.modalService.open(this.generatedSummaryModal, {
      backdrop: 'static',
      keyboard: false,
      centered: true,
      size: 'md',
      modalDialogClass: 'custom-modal-sm',
    });
  }

  cancelSummaryModal(): void {
    if (this.generateSummaryModalRef) {
      this.generateSummaryModalRef.dismiss();
      this.generateSummaryModalRef = null;
    }
  }

  saveGeneratedSummary(): void {
    const body = {
      summary: this.generatedSummaryForm.value.generatedSummary,
      patientId: this.summaryPatientId,
      programId: Number(this.generatedSummaryForm.value.programId) || 0,
      interactionTypeId: Number(this.generatedSummaryForm.value.interactionTypeId) || 0,
      callSid: this.summaryCallSid,
      callDuration: this.summaryCallDuration,
      callDateAndTime:
        this.summaryCallDateAndTime || this.formatCallDateAndTime(new Date()),
    };

    this.communicationService.saveGeneratedSummary(body).subscribe({
      next: (res: any) => {
        if (res) {
          const data = typeof res === 'string' ? JSON.parse(res) : res;
          this.toastService.successMessage(data?.message || 'Summary saved', true);
        }
        this.summaryProgramId = body.programId;
        this.summaryInteractionTypeId = body.interactionTypeId;
        this.summarySaved = true;
        this.cancelSummaryModal();
        this.openModal(this.callModal, {});
      },
      error: () => {
        this.toastService.errroMessage('Unable to save summary', true);
      },
    });
  }
}
